/* ============================================================================
   haiku-lightbox — ONE preview for the whole storefront

   The collection gallery and the product page each had their own idea of what
   opening a photograph means: the gallery morphs the thumbnail into a panel
   with GSAP Flip, the product page opened a separate dialog (.hi-lb). Same
   purpose, two implementations, free to drift — and they had. This is the one
   the owner chose, extracted so both surfaces can share it.

   STAGE 1 wires it to the product page only. The collection gallery keeps
   running its own Transition() until this is proven, so the surface that
   currently works best is never the thing being experimented on.

   IT REUSES THE GALLERY'S MARKUP AND CSS ON PURPOSE. The panel is `.content`
   with `.content__preview-img` inside it — the same class names the gallery
   emits — so every rule in haiku-gallery.css and haiku-preview.css applies
   without being copied. Copying them is exactly how the two drifted apart.

   OPEN/CLOSE IS `display` ON `.content`, because that is what the gallery does
   (haiku-gallery.js sets it via gsap) and what haiku-preview.js READS to
   decide whether a preview is open. Anything else here would look right and
   quietly break that reader.

   FAIL-SAFE. No GSAP, no Flip, or reduced motion: the panel still opens and
   closes, just without the morph. The photographs are always reachable.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuLightboxConfig || {};
  if (cfg.enabled === false) return;

  var DUR = (typeof cfg.duration === 'number' ? cfg.duration : 900) / 1000;
  var EASE = cfg.ease || 'power3.inOut';

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function libs() {
    return !!(window.gsap && window.Flip);
  }

  function boot() {
    var panel = document.querySelector('[data-hlb-panel]');
    if (!panel) return;

    var wrap = panel.querySelector('.content__preview-img');
    var img = wrap && wrap.querySelector('img');
    if (!wrap || !img) return;

    /* The product's own photographs are the source of truth for both the list
       and the morph origin — no second list to keep in step with the page. */
    var frames = Array.prototype.filter.call(
      document.querySelectorAll('[data-pe-gallery] [data-pe-frame]'),
      function (f) { return !!f.querySelector('img'); }
    );
    if (!frames.length) return;

    var srcs = frames.map(function (f) {
      var i = f.querySelector('img');
      return i.currentSrc || i.src;
    });
    /* Each photograph's shape, from the markup (the media's own width/height),
       so the figure can be sized before the panel's <img> has loaded. Reading
       naturalWidth alone, the first open found 0, morphed to the CSS default
       480x600, then snapped to the real 378x567 when the photo arrived ~0.7 s
       later (measured 2026-10-26; zy: "morphs to larger size but immediately
       shrinks"). Only the ratio is used, so the box is the same either way. */
    var dims = frames.map(function (f) {
      var i = f.querySelector('img');
      return [+i.getAttribute('width') || 0, +i.getAttribute('height') || 0];
    });

    var index = 0;
    var isOpen = false;
    var openedFrom = null;

    /* THE FIGURE TAKES THE PHOTOGRAPH'S OWN SHAPE.

       haiku-preview-fx draws its transition with COVER semantics — its own
       note says "object-fit: cover, in uv space, so the canvas frames exactly
       as the <img>". This panel shows the image with CONTAIN, so nothing is
       cropped on a product page. When the two disagree the canvas fills the
       figure while the image sits letterboxed inside it, and every change
       jumps between the two framings: the zoom in and out.

       Rather than pick one and lose either the crop or the effect, the figure
       is given the photograph's own aspect inside the 480x600 ceiling. With
       the box the same shape as the picture, contain and cover describe the
       same rectangle and there is nothing left to jump between.

       Written as custom properties because the CSS positions and sizes from
       them, so the box stays centred as it changes shape. */
    function boxOf(nw, nh) {
      /* The ceiling is the viewport's, not a desktop constant — on a phone a
         480px box is most of the screen and leaves no margin. */
      var vw = window.innerWidth, vh = window.innerHeight;
      var maxW = Math.min(480, Math.round(vw * 0.86));
      /* 62% on a phone, not 70%: the paging arrows live at the foot of the
         screen and the photograph must not reach them. Desktop keeps its
         600px ceiling, where the arrows do not exist. */
      var maxH = Math.min(600, Math.round(vh * (vw <= 900 ? 0.62 : 0.70)));
      var k = Math.min(maxW / nw, maxH / nh);
      return [Math.round(nw * k), Math.round(nh * k)];
    }

    function fitFigure() {
      var d = dims[index] || [];
      var nw = d[0] || img.naturalWidth, nh = d[1] || img.naturalHeight;
      if (!nw || !nh) return;
      var box = boxOf(nw, nh);
      panel.style.setProperty('--hlb-w', box[0] + 'px');
      panel.style.setProperty('--hlb-h', box[1] + 'px');
    }

    /* A FILE SIZED FOR THE PANEL, NOT FOR THE PAGE.

       `srcs` are the frames' own files, so the panel opens from cache. Since
       the frames' `sizes` were measured (2026-10-25) a phone's frame file is
       ~800px while the panel needs ~1000 (335px at 3x): the open is instant
       and the photograph a little soft. On the first open every entry is
       pointed at the smallest srcset candidate that covers the panel, and the
       open one is swapped in once that file has decoded — the same picture,
       sharper, so there is nothing to see but the sharpening. */
    function widthOf(url) {
      var m = /[?&]width=(\d+)/.exec(url || '');
      return m ? +m[1] : 0;
    }
    function bestFor(i) {
      var d = dims[i];
      var set = frames[i].querySelector('img').getAttribute('srcset') || '';
      if (!d || !d[0] || !d[1] || !set) return null;
      var need = boxOf(d[0], d[1])[0] * (window.devicePixelRatio || 1);
      var best = null;
      set.split(',').forEach(function (part) {
        var bits = part.trim().split(/\s+/);
        var w = parseInt(bits[1], 10);
        if (!bits[0] || !w) return;
        if (!best || (best.w < need ? w > best.w : (w >= need && w < best.w))) best = { url: bits[0], w: w };
      });
      return best ? { url: new URL(best.url, location.href).href, w: best.w } : null;
    }
    function sharpFor(i) {
      var best = bestFor(i);
      if (!best || best.w <= widthOf(srcs[i])) return srcs[i];
      return best.url;
    }

    /* THE FILES ARE READ AT THE FIRST OPEN, NOT AT BOOT.

       At boot a frame's currentSrc is often still empty — always for the
       lazy ones, and for the hero too when the script beats the image — so
       `srcs` fell back to the `src` attribute: the 1600px rendition, which
       the page never asked for. The first open then fetched it (Shopify
       renders an unrequested width on demand, slowly at times) while the
       morph waited 300 ms and then grew an empty box. Hence "not consistent"
       (zy, 2026-10-26). At the click, the clicked frame is on screen, so its
       currentSrc is a loaded file; frames not loaded yet get the file sized
       for the panel instead of the 1600px fallback. */
    function refreshSrcs() {
      for (var i = 0; i < frames.length; i++) {
        var im = frames[i].querySelector('img');
        var best = im.currentSrc ? null : bestFor(i);
        srcs[i] = im.currentSrc || (best ? best.url : im.src);
      }
    }

    var sharpened = false;
    function sharpen(open) {
      if (sharpened) return;
      sharpened = true;
      for (var i = 0; i < srcs.length; i++) {
        var url = sharpFor(i);
        if (url === srcs[i]) continue;
        if (i !== open) { srcs[i] = url; continue; }
        (function (j, from, to) {
          var pre = new Image();
          pre.onload = function () {
            var swap = function () {
              srcs[j] = to;
              if (isOpen && index === j && img.getAttribute('src') === from) img.setAttribute('src', to);
            };
            if (pre.decode) pre.decode().then(swap, swap); else swap();
          };
          pre.src = to;
        }(i, srcs[i], url));
      }
    }

    img.addEventListener('load', fitFigure);
    window.addEventListener('resize', fitFigure);

    function sourceImg(n) {
      var f = frames[n];
      return f ? f.querySelector('img') : null;
    }

    /* Changing photograph is a crossfade, not a cut. The next source is
       preloaded first — swapping src and fading in together shows a blank
       frame for however long the image takes, which reads as a flicker. */
    function paint(n, animate, dir) {
      index = (n % srcs.length + srcs.length) % srcs.length;
      panel.setAttribute('data-hlb-index', String(index));
      var next = srcs[index];
      if (img.getAttribute('src') === next) return;

      if (!animate) { img.setAttribute('src', next); return; }

      /* THE WEBGL TRANSITION IS ALREADY IN THIS THEME — USE IT.

         haiku-preview-fx.js draws the change between photographs in raw
         WebGL2 and exposes `window.haikuPreviewFx.transition(img, src, dir,
         fallback)`. Swapping `src` here with a GSAP crossfade bypassed it
         entirely, which is why the collection's change had a texture this one
         did not: it was never the same transition, only a similar-looking one.

         It declines by itself on reduced-motion and on touch, and takes a
         `fallback` for every path where WebGL2 is unavailable or the shader
         will not link — so the crossfade below is the fallback rather than a
         competing implementation. */
      var fallback = function () {
        if (!window.gsap || reduced()) { img.setAttribute('src', next); return; }
        var gsap = window.gsap;
        var pre = new Image();
        pre.onload = pre.onerror = function () {
          gsap.to(img, {
            opacity: 0, duration: 0.14, ease: 'none',
            onComplete: function () {
              img.setAttribute('src', next);
              gsap.to(img, { opacity: 1, duration: 0.22, ease: 'none' });
            }
          });
        };
        pre.src = next;
      };

      var fx = window.haikuPreviewFx;
      if (fx && typeof fx.transition === 'function') {
        try { fx.transition(img, next, dir || 1, fallback); return; }
        catch (e) { /* fall through */ }
      }
      fallback();
    }

    /* The morph. Record the panel figure's geometry while it is sitting on the
       thumbnail, put it back where it belongs, then animate from the record —
       which is Flip's whole idiom, and the same one haiku-gallery.js uses. */
    /* The morph waits for the panel's photograph to be decoded (≤ 300 ms):
       it is the frame's own file, so it is cached, but a different <img>
       still has to decode it, and for the first ~150 ms the morph used to
       carry an empty box. The figure is held invisible until then. */
    function morphIn(from) {
      if (!libs() || reduced() || !from) return;
      var gsap = window.gsap, Flip = window.Flip;
      var started = false;
      var start = function () {
        if (started) return;
        started = true;
        wrap.style.visibility = '';
        if (!isOpen) return;
        gsap.set(wrap, { clearProps: 'all' });
        Flip.fit(wrap, from, { absolute: true });
        var state = Flip.getState(wrap);
        gsap.set(wrap, { clearProps: 'all' });
        Flip.from(state, { duration: DUR, ease: EASE, absolute: true });
      };
      if (img.complete && img.naturalWidth > 0) { start(); return; }
      wrap.style.visibility = 'hidden';
      if (img.decode) img.decode().then(start, start);
      else img.addEventListener('load', start, { once: true });
      setTimeout(start, 300);
    }

    function morphOut(to, done) {
      if (!libs() || reduced() || !to) { done(); return; }
      var Flip = window.Flip;
      Flip.fit(wrap, to, { duration: DUR, ease: EASE, absolute: true, onComplete: done });
    }

    /* Hand the whole set to the fx layer so a change never waits on a fetch —
       once the first open's morph is over, not at boot. At boot it cost every product view a
       WebGL context, a shader compile and each photograph again at 1600px with
       mipmaps (measured 2026-10-25, Lighthouse mobile: ~2.1 MB and 2.3 s of
       main thread on a 6-photo product), for a lightbox most visitors never
       open. The collection preview warms the same way, on open. */
    var warmed = false;
    function warmFx() {
      if (warmed) return;
      warmed = true;
      if (window.haikuPreviewFx && typeof window.haikuPreviewFx.prefetch === 'function') {
        try { window.haikuPreviewFx.prefetch(srcs); } catch (e) {}
      }
    }

    function open(n, from) {
      if (isOpen) return;
      isOpen = true;
      openedFrom = from || sourceImg(n);
      if (window.gsap) window.gsap.set(img, { opacity: 1 });
      if (!sharpened) refreshSrcs();
      paint(n, false);
      fitFigure();
      sharpen(index);
      /* After the morph, not during it: warming builds a WebGL2 context,
         compiles the shader and decodes + uploads every photograph to the
         GPU on the main thread. Run at open (2026-10-25) it ate the morph's
         frames on zy's Mac — an empty panel, then the photo all at once. */
      setTimeout(warmFx, 300 + DUR * 1000 + 200);
      /* NOTHING CHANGES ON SCREEN UNTIL THE PHOTOGRAPH IS DECODED.

         On a first click the panel's <img> has its src but not yet a decoded
         frame (~17 ms on zy's Mac, longer on a slow phone). Showing the panel
         then — it covers the page — while the figure waited for the decode
         put one frame of bare background between the page and the morph:
         zy's "flicker on the first click, not the second" (video, 120 fps,
         2026-10-26: a single 17 ms frame, both times right after a load). So
         the panel and the morph start together, once the photo can be
         painted (≤ 300 ms, then anyway). A second click finds it decoded and
         opens in the same frame, as before. */
      var token = ++openToken;
      whenDecoded(function () {
        if (isOpen && token === openToken) show();
      });
    }

    var openToken = 0;
    function whenDecoded(fn) {
      if (img.complete && img.naturalWidth > 0) { fn(); return; }
      var done = false;
      var go = function () { if (!done) { done = true; fn(); } };
      if (img.decode) img.decode().then(go, go);
      else img.addEventListener('load', go, { once: true });
      setTimeout(go, 300);
    }

    function show() {
      /* `hp-open` as well as our own flag: haiku-preview.css gates .hp-nav
         and the scrim on it, so without it the controls exist in the DOM and
         compute `display: none`. Measured: nav present, 2 buttons, invisible. */
      document.documentElement.classList.add('hlb-open');
      document.documentElement.classList.add('hp-open');
      /* `hg-live` too. Measured on the collection: the open panel's html
         carries hg-live AND hp-open, the .hp-nav has NO inline style, and the
         photo's size comes from CSS min/max (480/720) with Flip only animating
         it. So the controls and the geometry are gated on this class, not
         written by script — which is why matching the ancestor and hp-open
         alone changed nothing. */
      document.documentElement.classList.add('hg-live');

      /* THE GATE THAT HID THE ARROWS.

         haiku-preview.css carries
           .content[data-hp-single="1"] .hp-nav { display: none }
         and haiku-preview.js sets that attribute when it believes the preview
         holds one photograph. It forms that belief by watching a
         `.gallery__slide` be clicked — which never happens here, because this
         lightbox opens the panel directly. So the arrows and the click-halves
         were built, inserted, and then hidden by a single attribute.

         The panel knows better: it has the product's whole media list. */
      if (srcs.length > 1) panel.setAttribute('data-hp-single', '0');
      else panel.setAttribute('data-hp-single', '1');
      panel.style.display = 'block';
      morphIn(openedFrom);

      /* THE ARROWS WERE HIDDEN BY AN INLINE STYLE, NOT A RULE.

         Diffing the two open panels finally said it plainly: on the collection
         the nav is `class="hp-nav"` and computes flex; here it carried
         `style="display: none"`. haiku-preview.js writes that itself when it
         believes the preview holds a single photograph — a belief it forms by
         watching a `.gallery__slide` be clicked, which never happens because
         this lightbox opens the panel directly.

         That is why four rounds of matching ancestors, classes and attributes
         changed nothing: none of them can outrank an inline style. The panel
         knows the truth — it holds the product's whole media list — so it
         clears the style and releases the attribute gate beside it.

         Re-asserted on a few beats because the layer re-evaluates after open. */
      if (srcs.length > 1) {
        var release = function () {
          if (!isOpen) return;
          panel.setAttribute('data-hp-single', '0');
          /* The arrows are not wanted on this panel (owner's call); only the
             click-halves are released. Paging still works by half, key and
             swipe. */
          var halves = panel.querySelectorAll('.hp-half');
          for (var i = 0; i < halves.length; i++) {
            if (halves[i].style.display === 'none') halves[i].style.display = '';
          }
        };
        release(); bindHalves();
        [80, 200, 500, 1100].forEach(function (t) { window.setTimeout(function () { release(); bindHalves(); }, t); });
      }
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;

      /* Morph back to the photograph being LOOKED AT, not the one opened from
         — open 1, page to 3, close, and it returns to 3. That photograph may
         be off-screen, so it is brought to the middle first.

         The scroll is instant, not smooth: the panel still covers the viewport
         at this moment, so the jump cannot be seen, and a smooth scroll would
         still be moving while Flip measured where to land. */
      /* RELEASE THE SCROLL LOCK BEFORE SCROLLING, not after.

         `hg-live` puts `overflow: hidden` on html and body — that is how the
         gallery takes scrolling away while a preview is open. Calling
         scrollIntoView underneath it did nothing useful, and removing the class
         afterwards reset the page to the top: measured, close left scrollY at 0
         with the target 1529px away. The photograph then flew off toward a
         frame that was nowhere on screen and vanished — exactly the behaviour
         reported.

         Unlock, let the layout settle, scroll, and only then morph: the frame
         has to be where it will finally be BEFORE Flip measures it. The panel
         still covers the viewport throughout, so the jump is never seen. */
      document.documentElement.classList.remove('hg-live');
      void document.documentElement.offsetHeight;

      var target = frames[index] || (openedFrom && openedFrom.closest('[data-pe-frame]'));
      if (target && target.scrollIntoView) {
        try { target.scrollIntoView({ block: 'center', inline: 'nearest' }); }
        catch (e) { target.scrollIntoView(); }
        void document.documentElement.offsetHeight;
      }

      var back = sourceImg(index) || openedFrom;
      morphOut(back, function () {
        panel.style.display = 'none';
        document.documentElement.classList.remove('hlb-open');
        document.documentElement.classList.remove('hp-open');
        document.documentElement.classList.remove('hg-live');
        if (window.gsap) window.gsap.set(wrap, { clearProps: 'all' });
        /* Return focus to the photograph that was opened, not to the top of
           the document. */
        if (openedFrom && openedFrom.closest) {
          var f = openedFrom.closest('[data-pe-frame]');
          if (f && f.focus) { try { f.focus({ preventScroll: true }); } catch (e) {} }
        }
      });
    }

    function step(d) {
      if (!isOpen) return;
      lastDir = d;
      paint(index + d, true, d);
    }

    /* --- wiring ---------------------------------------------------------- */

    frames.forEach(function (frame, n) {
      frame.setAttribute('tabindex', '0');
      frame.setAttribute('role', 'button');
      frame.addEventListener('click', function (e) {
        if (e.defaultPrevented) return;
        e.preventDefault();
        open(n, frame.querySelector('img'));
      });
      frame.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(n, frame.querySelector('img')); }
      });
    });

    var prevBtn = panel.querySelector('[data-hlb-prev]');
    var nextBtn = panel.querySelector('[data-hlb-next]');
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); step(1); });
    /* One photograph needs no paging. */
    if (srcs.length < 2) {
      var nav = panel.querySelector('[data-hlb-nav]');
      if (nav) nav.style.display = 'none';
    }

    var closeBtn = panel.querySelector('[data-hlb-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    });

    /* THE THEME'S HALVES, DRIVEN BY THIS FILE.

       haiku-preview.js inserts real `.hp-half` buttons — with the cursor
       treatment and the accessible labels — and pages them from its own state.
       That state is the ACTIVE SLIDE, which never gets set here because this
       lightbox opens the panel directly rather than by clicking a slide.
       Measured: a click on its half changed nothing at all.

       So the buttons are kept and the behaviour is supplied. Binding here
       cannot double-advance precisely because its own handler is inert — which
       was checked, not assumed, after the opposite mistake made it advance
       two at a time. */
    var bindHalves = function () {
      var map = [['.hp-half--prev', -1], ['.hp-half--next', 1]];
      for (var i = 0; i < map.length; i++) {
        (function (sel, d) {
          var el = panel.querySelector(sel);
          if (!el || el.__hlbBound) return;
          el.__hlbBound = true;
          el.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            step(d);
          });
        }(map[i][0], map[i][1]));
      }
    };

    /* NO CLICK-TO-PAGE ON THE FIGURE.

       haiku-preview.js inserts real `.hp-half` buttons over the panel and
       pages with them. This file used to do the same on the figure, so a
       single click ran both and the preview advanced TWO photographs — which
       is what "photos are jumping around" was. The theme's halves are the
       ones with the cursor treatment and the accessible labels, so they win
       and this goes.

       EXCEPT WHERE THOSE HALVES DO NOT EXIST. haiku-preview.js returns at
       ≤900px, so on a phone there are no halves and no arrows, and a tap on
       the photo did nothing — paging was swipe only (measured 2026-10-26).
       zy: tap the left or right side to go back or forward. Gated on the
       halves being absent at the moment of the tap, not on a width, so the
       two can never both run. A swipe produces no click, so it still pages
       once, through the swipe handler below. */
    wrap.addEventListener('click', function (e) {
      if (!isOpen || srcs.length < 2) return;
      if (panel.querySelector('.hp-half')) return;
      var r = wrap.getBoundingClientRect();
      step(e.clientX < r.left + r.width / 2 ? -1 : 1);
    });

    // Swipe, for the phone.
    var x0 = null;
    wrap.addEventListener('touchstart', function (e) {
      x0 = e.touches && e.touches[0] ? e.touches[0].clientX : null;
    }, { passive: true });
    wrap.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var t = e.changedTouches && e.changedTouches[0];
      if (t && Math.abs(t.clientX - x0) > 40) step(t.clientX < x0 ? 1 : -1);
      x0 = null;
    }, { passive: true });

    /* WHOEVER CHANGES THE PHOTOGRAPH, THE INDEX FOLLOWS.

       The theme's `.hp-half` buttons page through haiku-preview.js, which
       knows nothing about this file's index — so after removing the duplicate
       handler the picture changed while `index` stayed put, and closing would
       have morphed back to the wrong frame.

       Rather than add a second handler back (which is what made it advance
       twice), the index is derived from the <img> itself. One source of truth,
       and it stays right no matter which layer did the paging.

       ⚠️ REVERSED 2026-10-26: THE NEWEST REQUEST WINS. Every page now goes
       through step()/paint() (the halves are bound to step() above, and
       haiku-preview's own half handler was measured inert), so the only
       writers left are this file's own transitions — and a transition that
       a quicker tap or click overtook still lands its photograph when it
       finishes. Adopting that put the index back on the photo the visitor
       had just left (measured: tap forward, tap back 1.2 s later, stuck on
       the second photo). So a src that is not the requested one is steered
       back to the requested one; the transition there ends on srcs[index]
       and settles. */
    var lastDir = 1;
    var steer = 0;
    var mo = new MutationObserver(function () {
      var cur = img.getAttribute('src');
      if (!cur || !isOpen || cur === srcs[index]) return;
      clearTimeout(steer);
      steer = setTimeout(function () {
        if (isOpen && img.getAttribute('src') !== srcs[index]) paint(index, true, lastDir);
      }, 0);
    });
    mo.observe(img, { attributes: true, attributeFilter: ['src'] });

    window.haikuLightbox = { open: open, close: close, step: step };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}());
